import { Column, ColumnDef, Row } from "@tanstack/react-table";
import {
  memo,
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Button } from "../../button";
import { cn } from "../../../utils/cn";
import {
  EXPORT_TYPES,
  PendingStatuses,
  REPORT_NAMES,
  STATUS_IDS,
  STATUS_INDICATOR_COLUMN,
  Statuses,
  TABLE_IDS,
  tableColumnsAr,
  tableColumnsEn,
  USER_ROLES_IDS,
  VisitorDetails,
} from "../../../constants/common";
import {
  downloadFile,
  formattedDate,
  formattedDateTimeWithoutSec,
} from "../../../lib/functions";
import { ArrowUpDown } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { APP_ROUTES } from "../../../routes";
import TableWrapper from "../TableWrapper";
import useAuthStore from "../../../store/Auth";
import ApprovalStatusesModal from "../../modal/ApprovalStatusesModal";
import { useGetAppointmentApprovalStatusesById } from "../../../hooks/visitor/query";
import { useTranslation } from "react-i18next";
import CancelAppointmentModal from "../../modal/CancelAppointmentModal";
import { ExportApprovedVisitRequestsForExcelPDFBody } from "../../../hooks/visitor/interface";
import toast, { Toaster } from "react-hot-toast";
import StatusCodes from "../../../constants/StatusCodes";
import {
  useExportApproveVisitRequestsForExcel,
  useExportApproveVisitRequestsForPDF,
  useExportSearchVisitRequestsForExcel,
  useExportSearchVisitRequestsForPDF,
} from "../../../hooks/visitor/mutation";

interface SearchPassTableProps {
  allVisitorAppointments: never[];
  page: number;
  pages: number;
  setPage: Dispatch<SetStateAction<number>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  count: number;
  tableId: string;
  exportBody?: ExportApprovedVisitRequestsForExcelPDFBody;
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
}

const SearchPassTable = ({
  allVisitorAppointments,
  pages,
  page,
  setPage,
  search,
  count,
  setSearch,
  tableId,
  exportBody,
  pageSize,
  setPageSize,
}: SearchPassTableProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { t, i18n } = useTranslation();

  const [isApprovalStatusesModalOpen, setIsApprovalStatusesModalOpen] =
    useState(false);

  const [isCancelAppointmentModalOpen, setIsCancelAppointmentModalOpen] =
    useState<boolean>(false);
  const [selectedDeleteAppointmentId, setSelectedDeleteAppointmentId] =
    useState("0");

  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>([]);

  const [selectedAppointmentId, setSelectedAppointmentId] = useState(0);

  const { mutateAsync: ExportSearchVisitRequestsForExcel } =
    useExportSearchVisitRequestsForExcel();

  const { mutateAsync: ExportApproveVisitRequestsForExcel } =
    useExportApproveVisitRequestsForExcel();

  const { mutateAsync: ExportSearchVisitRequestsForPDF } =
    useExportSearchVisitRequestsForPDF();

  const { mutateAsync: ExportApproveVisitRequestsForPDF } =
    useExportApproveVisitRequestsForPDF();

  const { data: approvalStatuses } = useGetAppointmentApprovalStatusesById({
    id: selectedAppointmentId,
  });

  const user = useAuthStore((state) => state.user);

  const handleViewApprovalStatusDetails = useCallback(
    (id: string) => {
      if (user?.n_RoleID === USER_ROLES_IDS.ADMIN) {
        setSelectedAppointmentId(Number(id));
        setIsApprovalStatusesModalOpen(true);
      }
    },
    [user?.n_RoleID]
  );

  const handleCancelAppointment = useCallback((appointmentId: string) => {
    setIsCancelAppointmentModalOpen(true);
    setSelectedDeleteAppointmentId(appointmentId);
  }, []);

  const searchPassColumns: ColumnDef<VisitorDetails>[] = useMemo(
    () =>
      [
        {
          accessorKey: STATUS_INDICATOR_COLUMN.CURRENT_ROLE_STATUS,
          header: () => <></>,
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            if (row.original.currentRoleAppointmentStatusEN) {
              const isReqPendingInfo =
                row.original.currentRoleAppointmentStatusEN ===
                  Statuses.REQUEST_INFO ||
                row.original.currentRoleAppointmentStatusEN ===
                  Statuses.PENDING;

              const isPendingWithRole =
                row.original.currentRoleAppointmentStatusEN?.split(" - ")[0] ===
                Statuses.PENDING;

              return (
                <div
                  className={cn(
                    "text-center rounded-md mx-auto w-5 h-5",
                    row.original.currentRoleAppointmentStatusEN ===
                      Statuses.APPROVED
                      ? "bg-green-600"
                      : isPendingWithRole
                      ? "bg-yellow-500"
                      : row.original.currentRoleAppointmentStatusEN ===
                        Statuses.REQUEST_INFO
                      ? "bg-purple-tertiary"
                      : "bg-red-500"
                  )}
                >
                  <p className="text-white">
                    {i18n.language === "en"
                      ? !isReqPendingInfo
                        ? row.original.currentRoleAppointmentStatusEN.split(
                            ""
                          )[0]
                        : row.original.currentRoleAppointmentStatusEN.split(
                            ""
                          )[0]
                      : !isReqPendingInfo
                      ? row.original.currentRoleAppointmentStatusAR.split("")[0]
                      : row.original.currentRoleAppointmentStatusAR.split(
                          ""
                        )[0]}
                  </p>
                </div>
              );
            }
          },
        },
        {
          accessorKey: STATUS_INDICATOR_COLUMN.ADMIN_STATUS,
          header: () => <></>,
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <div
                className={cn(
                  "text-center rounded-md mx-auto w-5 h-5",
                  row.original.overAllAppointmentStatusEN === Statuses.APPROVED
                    ? "bg-green-600"
                    : row.original.overAllAppointmentStatusEN ===
                      PendingStatuses.PENDING_STAFF
                    ? "bg-yellow-500"
                    : row.original.overAllAppointmentStatusEN ===
                      PendingStatuses.PENDING_AUTHORIZED_PERSON
                    ? "bg-orange-light"
                    : row.original.overAllAppointmentStatusEN ===
                      PendingStatuses.PENDING_CYBER_SECURITY
                    ? "bg-purple-dark"
                    : row.original.overAllAppointmentStatusEN ===
                      PendingStatuses.PENDING_SECURITY
                    ? "bg-purple-orchid"
                    : row.original.overAllAppointmentStatusEN ===
                      PendingStatuses.PENDING_SECURITY_MANAGER
                    ? "bg-blue-primary"
                    : row.original.overAllAppointmentStatusEN ===
                      Statuses.REQUEST_INFO
                    ? "bg-purple-tertiary"
                    : "bg-red-500"
                )}
              >
                <p className="text-sm text-white whitespace-normal">
                  {i18n.language === "en"
                    ? row.original.overAllAppointmentStatusEN?.split("")[0]
                    : row.original.overAllAppointmentStatusAR?.split("")[0]}
                </p>
              </div>
            );
          },
        },
        {
          accessorKey: "s_AppointmentCode",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("referenceNumber")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span
                onClick={() =>
                  navigate(
                    `${
                      location.pathname === APP_ROUTES.APPROVE_VISIT_REQUEST
                        ? APP_ROUTES.APPROVE_REQUEST
                        : APP_ROUTES.UPDATE_REQUEST
                    }?ID=${row?.original?.n_AppointmentID.toString()}`
                  )
                }
                className="text-blue-800 underline cursor-pointer"
              >
                {row.original.s_AppointmentCode}
              </span>
            );
          },
        },
        {
          accessorKey: "s_ApplyFor_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("applyFor")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_ApplyFor_En
                  : row.original.s_ApplyFor_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_VisitorName_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("name")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_VisitorName_En}</span>;
          },
        },
        {
          accessorKey: "s_Sponsor",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("companyName")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_Sponsor}</span>;
          },
        },
        {
          accessorKey: "dt_AppointmentStartTime",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("visitStartDate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {formattedDateTimeWithoutSec(
                  row.original.dt_AppointmentStartTime
                )}
              </span>
            );
          },
        },
        {
          accessorKey: "dt_AppointmentEndTime",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("visitEndDate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {formattedDateTimeWithoutSec(
                  row.original.dt_AppointmentEndTime
                )}
              </span>
            );
          },
        },
        {
          accessorKey: "currentRoleAppointmentStatusEN",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("currentStatus")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            if (row.original.currentRoleAppointmentStatusEN) {
              return (
                <div
                  className={cn(
                    "text-center rounded-lg py-0.5 mx-auto w-[128px]",
                    row.original.currentRoleAppointmentStatusEN ===
                      Statuses.APPROVED
                      ? "bg-green-600"
                      : row.original.currentRoleAppointmentStatusEN ===
                        Statuses.PENDING
                      ? "bg-yellow-500"
                      : row.original.currentRoleAppointmentStatusEN ===
                        Statuses.REQUEST_INFO
                      ? "bg-purple-tertiary"
                      : "bg-red-500"
                  )}
                >
                  <p className="text-white whitespace-normal">
                    {i18n.language === "en"
                      ? row.original.currentRoleAppointmentStatusEN
                      : row.original.currentRoleAppointmentStatusAR}
                  </p>
                </div>
              );
            }
          },
        },
        {
          accessorKey: "overAllAppointmentStatusEN",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("finalStatus")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <div
                onClick={() =>
                  handleViewApprovalStatusDetails(row.original.n_AppointmentID)
                }
                className={cn(
                  "text-center rounded-lg py-0.5 mx-auto w-[128px]",
                  row.original.overAllAppointmentStatusEN === Statuses.APPROVED
                    ? "bg-green-600"
                    : row.original.overAllAppointmentStatusEN ===
                      PendingStatuses.PENDING_STAFF
                    ? "bg-yellow-500"
                    : row.original.overAllAppointmentStatusEN ===
                      PendingStatuses.PENDING_AUTHORIZED_PERSON
                    ? "bg-orange-light"
                    : row.original.overAllAppointmentStatusEN ===
                      PendingStatuses.PENDING_CYBER_SECURITY
                    ? "bg-purple-dark"
                    : row.original.overAllAppointmentStatusEN ===
                      PendingStatuses.PENDING_SECURITY
                    ? "bg-purple-orchid"
                    : row.original.overAllAppointmentStatusEN ===
                      PendingStatuses.PENDING_SECURITY_MANAGER
                    ? "bg-blue-primary"
                    : row.original.overAllAppointmentStatusEN ===
                      Statuses.REQUEST_INFO
                    ? "bg-purple-tertiary"
                    : "bg-red-500",
                  user?.n_RoleID === USER_ROLES_IDS.ADMIN && "cursor-pointer"
                )}
              >
                <p className="text-sm text-white whitespace-normal">
                  {i18n.language === "en"
                    ? row.original.overAllAppointmentStatusEN
                    : row.original.overAllAppointmentStatusAR}
                </p>
              </div>
            );
          },
        },
        {
          accessorKey: "s_MobileNo",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("mobileNumber")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_MobileNo}</span>;
          },
        },
        {
          accessorKey: "s_Email",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("email")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_Email}</span>;
          },
        },
        {
          accessorKey: "dt_DateOfBirth",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("dateOfBirth")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {row.original.dt_DateOfBirth
                  ? formattedDate(new Date(row.original.dt_DateOfBirth), true)
                  : ""}
              </span>
            );
          },
        },
        {
          accessorKey: "s_Nationality_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("nationality")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_Nationality_En
                  : row.original.s_Nationality_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_HostName",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("hostName")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_HostName}</span>;
          },
        },
        {
          accessorKey: "s_HostEmail",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("hostEmail")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_HostEmail}</span>;
          },
        },
        {
          accessorKey: "s_HostMobileNo",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("hostMobileNumber")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
        },
        {
          accessorKey: "s_LocationName_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("location")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_LocationName_En
                  : row.original.s_LocationName_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_BuildingName_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("building")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_BuildingName_En
                  : row.original.s_BuildingName_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_GateName_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("gate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_GateName_En
                  : row.original.s_GateName_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_MeetingRoomName_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("meetingRoom")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_MeetingRoomName_En
                  : row.original.s_MeetingRoomName_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_DepartmentName_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("department")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_DepartmentName_En
                  : row.original.s_DepartmentName_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_VisitType_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("visitType")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_VisitType_En
                  : row.original.s_VisitType_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_PurposeEn",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("visitPurpose")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_PurposeEn
                  : row.original.s_PurposeAr}
              </span>
            );
          },
        },
        {
          accessorKey: "s_DocumentType_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("documentType")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_DocumentType_En
                  : row.original.s_DocumentType_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_Iqama",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("iqama")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_Iqama}</span>;
          },
        },
        {
          accessorKey: "dt_IqamaExpiry",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("iqamaExpiryDate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {row.original.dt_IqamaExpiry
                  ? formattedDate(new Date(row.original.dt_IqamaExpiry), true)
                  : ""}
              </span>
            );
          },
        },
        {
          accessorKey: "eidNumber",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("nationalID")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.eidNumber}</span>;
          },
        },
        {
          accessorKey: "dt_EIDExpiryDate",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("nationalIDExpiryDate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {row.original.dt_EIDExpiryDate
                  ? formattedDate(new Date(row.original.dt_EIDExpiryDate), true)
                  : ""}
              </span>
            );
          },
        },
        {
          accessorKey: "passportNumber",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("passportNumber")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.passportNumber}</span>;
          },
        },
        {
          accessorKey: "dt_PassportExpiryDate",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("passportExpiryDate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {row.original.dt_PassportExpiryDate
                  ? formattedDate(
                      new Date(row.original.dt_PassportExpiryDate),
                      true
                    )
                  : ""}
              </span>
            );
          },
        },
        {
          accessorKey: "s_VisaNo",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("visaNumber")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_VisaNo}</span>;
          },
        },
        {
          accessorKey: "dt_VisaExpiryDate",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("visaExpiryDate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {row.original.dt_VisaExpiryDate
                  ? formattedDate(
                      new Date(row.original.dt_VisaExpiryDate),
                      true
                    )
                  : ""}
              </span>
            );
          },
        },
        {
          accessorKey: "s_VehicleNo",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("vehicleNo")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_VehicleNo || "-"}</span>;
          },
        },
        {
          accessorKey: "s_IsVehicleAllowed_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("vehiclePermit")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <div
                className={cn(
                  "text-center rounded-lg py-0.5 mx-auto max-w-[128px] w-[128px]",
                  row.original.s_VehicleNo
                    ? row.original.n_IsVehicleAllowed === -1
                      ? "bg-yellow-500"
                      : row.original.n_IsVehicleAllowed === 1
                      ? "bg-green-600"
                      : "bg-red-500"
                    : "bg-transparent",
                  user?.n_RoleID === USER_ROLES_IDS.ADMIN && "cursor-pointer"
                )}
              >
                <p
                  className={cn(
                    "text-sm text-white whitespace-normal",
                    row.original.s_VehicleNo ? "text-white" : "text-[#828282]"
                  )}
                >
                  {row.original.s_VehicleNo
                    ? i18n.language === "en"
                      ? row.original.s_IsVehicleAllowed_En
                      : row.original.s_IsVehicleAllowed_Ar
                    : "-"}
                </p>
              </div>
            );
          },
        },
        {
          accessorKey: "s_CreatedBy",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("createdBy")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_CreatedBy}</span>;
          },
        },
        {
          accessorKey: "dt_CreatedDate",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("createdDate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {formattedDateTimeWithoutSec(row.original.dt_CreatedDate)}
              </span>
            );
          },
        },
        {
          accessorKey: "action",
          header: t("action"),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <div className="flex gap-1 items-center">
                <Button
                  onClick={() =>
                    handleCancelAppointment(row.original.n_AppointmentID)
                  }
                  disabled={
                    new Date(row.original?.dt_AppointmentEndTime) <=
                      new Date() ||
                    row?.original?.approvalStatus === STATUS_IDS.CANCELED ||
                    row?.original?.approvalStatus === STATUS_IDS.REJECTED ||
                    row?.original?.approvalStatus === STATUS_IDS.EXPIRED
                  }
                  className="py-0.5"
                >
                  {t("cancel")}
                </Button>
              </div>
            );
          },
        },
      ].filter((column) => {
        let excludeKeys = [];

        if (
          user?.n_RoleID === USER_ROLES_IDS.ADMIN ||
          user?.n_RoleID === USER_ROLES_IDS.SECURITY_MANAGER ||
          user?.n_RoleID === USER_ROLES_IDS.RECEPTION
        ) {
          excludeKeys.push(
            "currentRoleAppointmentStatusEN",
            STATUS_INDICATOR_COLUMN.CURRENT_ROLE_STATUS
          );
        } else {
          excludeKeys.push(STATUS_INDICATOR_COLUMN.ADMIN_STATUS);
        }

        if (tableId !== TABLE_IDS.SEARCH_VISIT_REQUEST) {
          excludeKeys.push("action");
        }

        return column.accessorKey
          ? !excludeKeys.includes(column.accessorKey)
          : true;
      }),
    [
      t,
      i18n.language,
      navigate,
      location.pathname,
      user?.n_RoleID,
      handleViewApprovalStatusDetails,
      handleCancelAppointment,
      tableId,
    ]
  );

  const onExport = useCallback(
    async (type: EXPORT_TYPES) => {
      if (!exportBody) return;

      if (visibleColumnKeys.length === 0) {
        toast.error(t("noColumnSelectedForExport"));
        return;
      }

      try {
        const body = {
          ...exportBody,
          ExportOptions: visibleColumnKeys.map((key) => ({
            ColumnKey: key,
            ColumnNameEn: tableColumnsEn[key],
            ColumnNameAr: tableColumnsAr[key],
            CurrentLanguage: i18n.language,
          })),
        };

        let res;
        if (tableId === TABLE_IDS.SEARCH_VISIT_REQUEST) {
          if (type === EXPORT_TYPES.EXCEL) {
            res = await ExportSearchVisitRequestsForExcel(body);
          } else {
            res = await ExportSearchVisitRequestsForPDF(body);
          }
        } else {
          if (type === EXPORT_TYPES.EXCEL) {
            res = await ExportApproveVisitRequestsForExcel(body);
          } else {
            res = await ExportApproveVisitRequestsForPDF(body);
          }
        }

        downloadFile(
          res,
          tableId === TABLE_IDS.SEARCH_VISIT_REQUEST
            ? REPORT_NAMES.SEARCH_VISIT_REQUESTS
            : REPORT_NAMES.APPROVED_VISIT_REQUESTS
        );
        toast.success(t("exportSuccess"));
      } catch (error: any) {
        if (error.statusCode === StatusCodes.NOT_FOUND) {
          toast.error(t("noDataToExport"));
        } else {
          toast.error(t("somethingWentWrong"));
        }
      }
    },
    [
      ExportApproveVisitRequestsForExcel,
      ExportSearchVisitRequestsForExcel,
      ExportSearchVisitRequestsForPDF,
      ExportApproveVisitRequestsForPDF,
      exportBody,
      i18n.language,
      t,
      tableId,
      visibleColumnKeys,
    ]
  );

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <TableWrapper
        data={allVisitorAppointments}
        columns={searchPassColumns}
        search={search}
        setSearch={setSearch}
        page={page}
        setPage={setPage}
        totalPages={pages}
        count={count}
        isSearch
        tableId={tableId}
        isHideStatusIndicator
        isExportExcel
        onExport={onExport}
        setVisibleColumnKeys={setVisibleColumnKeys}
        hideColumns={{
          s_ApplyFor_En: false,
          s_HostEmail: false,
          eidNumber: false,
          dt_EIDExpiryDate: false,
          passportNumber: false,
          dt_PassportExpiryDate: false,
          s_VisaNo: false,
          dt_VisaExpiryDate: false,
          s_CreatedBy: false,
          dt_CreatedDate: false,
        }}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />

      <ApprovalStatusesModal
        approvalStatuses={approvalStatuses}
        isOpen={isApprovalStatusesModalOpen}
        onOpenChange={() => setIsApprovalStatusesModalOpen(false)}
      />

      <CancelAppointmentModal
        selectedAppointmentId={selectedDeleteAppointmentId}
        setSelectedAppointmentId={setSelectedDeleteAppointmentId}
        isOpen={isCancelAppointmentModalOpen}
        onOpenChange={() => setIsCancelAppointmentModalOpen(false)}
      />
    </>
  );
};

export default memo(SearchPassTable);
